import {
  Component,
  computed,
  EventEmitter,
  Input,
  Output,
  WritableSignal,
  signal,
} from '@angular/core';
import { Approval } from 'ng2-app/models/dto/approval/approval.model';
import { Store } from '@ngxs/store';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LenderUserState } from 'ng2-app/store/lender-user/lender-user/lender-user.state';
import { take } from 'rxjs/operators';
import { UpdatePublishApproval, UpdateUnpublishApproval } from 'ng2-app/store/offers/approvals.actions';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'ng2-app/modules/confirmation-dialog/confirmation-dialog.component';
import { ApprovalSnackBarDataComponent } from 'ng2-app/approval/approval-snack-bar-data/approval-snack-bar-data.component';
import { ApprovalSnackBarType } from 'ng2-app/approval/approval-snack-bar-data/approval-snack-bar-data.interface';
import { BorrowerState } from 'ng2-app/store/borrower/borrower/borrower.state';
import { DowngradeDealStatus } from 'app/store/deal/deal/deal.actions';
import { DeclineReasonsDialogSmartComponent } from 'app/components/decline-reasons/decline-reasons-dialog-smart.component';

@Component({
  selector: 'app-approval-menu-smart',
  templateUrl: './approval-menu-smart.component.html',
  standalone: false
})
export class ApprovalMenuSmartComponent {
  @Input() set approval(approval: Approval) {
    if (!this._approvalSignal) {
      this._approvalSignal = signal(approval);
    } else {
      this._approvalSignal.set(approval);
    }
  }
  @Input() hideCreateOptionButton: boolean = false;
  @Output() createOption = new EventEmitter<Approval>();
  get approval(): Approval {
    return this._approvalSignal();
  }
  _approvalSignal: WritableSignal<Approval>;
  borrower = this.store.selectSignal( BorrowerState.borrower );
  permissions = this.store.selectSignal( LenderUserState.hasPermissions([
    'canPublishOffersToPortal',
    'publishApprovalsToPortal',
    'ff:offerCuration',
  ]));

  approvalMenuItems = computed(() => {
    const permissions = this.permissions();
    const borrower = this.borrower();
    const isStandardBorrower = !borrower?.user_experiments?.some((e) => e.experimentId === 34)
      && !borrower?.isEmbedded
      && !borrower?.isIntelligentLending;
    const canSeePublishApproval = permissions['canPublishOffersToPortal']
      && permissions['publishApprovalsToPortal']
      && !isStandardBorrower
      && !this.approval.isTest;

    return [
      {
        text: 'Create option',
        isVisible: !this.hideCreateOptionButton && permissions['ff:offerCuration'],
        enabled: true,
        onClick: this.createOfferOption.bind(this),
      },
      {
        text: 'Publish to portal',
        isVisible: canSeePublishApproval,
        enabled: !Boolean(this.approval.publishedByLenderUserId && this.approval.publishedToBorrower),
        onClick: this.publishApproval.bind(this),
      },
      {
        text: 'Remove from portal',
        isVisible: permissions['canPublishOffersToPortal'],
        enabled: Boolean(this.approval.publishedByLenderUserId && this.approval.publishedToBorrower),
        onClick: this.unpublishApproval.bind(this),
      },
      {
        text: 'Decline Approval',
        isVisible: !this.approval.declineReasons?.length,
        enabled: !this.approval.declineReasons?.length,
        onClick: this.confirmDecline.bind(this),
      },
      {
        text: 'Un-decline Approval',
        isVisible: this.approval.declineReasons?.length > 0,
        enabled: this.approval.declineReasons?.length > 0,
        onClick: this.confirmDecline.bind(this),
      },
    ].filter(item => item.isVisible);
  });

  visibleCount: number = 0;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) { }

  private createOfferOption() {
    this.createOption.emit(this.approval);
  }

  private publishApproval() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        showHeader: true,
        headerText: 'Publish this approval to portal?',
        confirmationPromptText: 'Once published the borrower will be able to view this approval.',
        showCancelButton: true,
        confirmButtonText: 'Publish'
      }
    });

    dialogRef.componentInstance.confirm.pipe(take(1)).subscribe(() => {
      this.store
        .dispatch(new UpdatePublishApproval(this.approval.id))
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.openApprovalSnackBar(true, ApprovalSnackBarType.PUBLISH);
          },
          error: () => {
            this.openApprovalSnackBar(false, ApprovalSnackBarType.PUBLISH);
          },
        });

      dialogRef.close();
    });
  }

  private unpublishApproval() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        showHeader: true,
        headerText: 'Remove approval from portal',
        confirmationPromptText:
          'Once removed the borrower will no longer be able to view this approval and it will revert back to a draft in pipeline.',
        showCancelButton: true,
        confirmButtonText: 'Remove'
      }
    });

    dialogRef.componentInstance.confirm.pipe(take(1)).subscribe(() => {
      this.store
        .dispatch(new UpdateUnpublishApproval(this.approval.id))
        .pipe(take(1))
        .subscribe({
          next: () => {
            this.openApprovalSnackBar(
              true,
              ApprovalSnackBarType.REMOVE_FROM_PORTAL
            );
            if (this.approval.dealId) this.store.dispatch(new DowngradeDealStatus(this.approval.dealId));
          },
          error: () => {
            this.openApprovalSnackBar(
              false,
              ApprovalSnackBarType.REMOVE_FROM_PORTAL
            );
          },
        });

      dialogRef.close();
    });
  }

  private confirmDecline() {
    const dialogRef = this.dialog.open(DeclineReasonsDialogSmartComponent, {
      width: '400px',
      data: {
        option: this.approval,
        optionType: 'approval',
      }
    });
    dialogRef.componentInstance.optionDeclined.pipe(
      take(1)
    ).subscribe( () => dialogRef.close() );
  }

  private openApprovalSnackBar(isSuccess: boolean, type: ApprovalSnackBarType) {
    this.snackBar.openFromComponent(ApprovalSnackBarDataComponent, {
      data: {
        approval: this.approval,
        isSuccess: isSuccess,
        type,
      },
      duration: 3000,
    });
  }
}
